import { X, Target, Euro, TrendingUp, BarChart3, Users, Building2 } from 'lucide-react';
import { formatCurrency } from '../../utils/format';
import { getStatusBadgeClass } from '../../utils/colors';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { getMonthObjectiveLabel, getMonthsLabel } from '../../utils/period';

interface Negotiator {
  id: string;
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
}

interface Agency {
  name: string;
  code: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  negotiators: Negotiator[];
}

interface Director {
  id: string;
  name: string;
  company: string;
  total_sales: number;
  potential_sales: number;
  sales_count: number;
  annual_objective: number;
  accounting_objective: number;
  agencies: Agency[];
}

interface DirectorDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  director: Director | null;
  period: 'year' | 'quarter' | 'month';
  selectedPeriod: string;
  fiscalYear: number;
}

function getCompanyBg(company: string) {
  switch (company) {
    case 'KI':     return 'bg-blue-700';
    case 'KA':     return 'bg-emerald-700';
    case 'DRYADE': return 'bg-violet-700';
    default:       return 'bg-slate-600';
  }
}

function getCompanyBadgeClass(company: string) {
  switch (company) {
    case 'KI':     return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'KA':     return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'DRYADE': return 'bg-violet-50 text-violet-700 border-violet-200';
    default:       return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

function getInitials(name: string) {
  const words = name.trim().split(/\s+/);
  return words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : name.substring(0, 2).toUpperCase();
}

function moneyPill(value: number, pillClass: string) {
  if (!value || value === 0) return <span className="text-slate-300 text-sm">—</span>;
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${pillClass}`}>
      {formatCurrency(value)}
    </span>
  );
}

function completionPill(rate: number) {
  const cls =
    rate >= 100 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
    rate >= 75  ? 'bg-blue-50 text-blue-700 border-blue-200' :
    rate >= 50  ? 'bg-amber-50 text-amber-700 border-amber-200' :
                  'bg-red-50 text-red-700 border-red-200';
  const bar =
    rate >= 100 ? 'bg-emerald-400' :
    rate >= 75  ? 'bg-blue-400' :
    rate >= 50  ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border w-fit ${cls}`}>
        {rate.toFixed(1)}%
      </span>
      <div className="h-1 w-16 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${Math.min(rate, 100)}%` }} />
      </div>
    </div>
  );
}

export default function DirectorDetailsModal({
  isOpen, onClose, director, period, selectedPeriod, fiscalYear
}: DirectorDetailsModalProps) {
  useEscapeKey(() => { if (isOpen) onClose(); });

  if (!isOpen || !director) return null;

  // L'objectif reçu est déjà pro-ratisé pour la période par useDirectionStats
  const calculateObjective = (annualObjective: number) => annualObjective;

  const getPeriodLabel = () => {
    if (period === 'year') return `Année ${fiscalYear}-${fiscalYear + 1}`;
    if (period === 'quarter') {
      const labels: Record<string, string> = {
        '1': '1er trimestre (Avr–Juin)',
        '2': '2ème trimestre (Juil–Sep)',
        '3': '3ème trimestre (Oct–Déc)',
        '4': '4ème trimestre (Jan–Mar)',
      };
      return labels[selectedPeriod] ?? '';
    }
    return getMonthsLabel(selectedPeriod, fiscalYear);
  };

  const objective = calculateObjective(director.annual_objective);
  const completionRate = objective ? (director.total_sales / objective) * 100 : 0;
  const remaining = objective - director.total_sales;

  const objectiveLabel = period === 'year' ? 'Objectif Annuel' : period === 'quarter' ? 'Objectif Trim.' : `Objectif ${getMonthObjectiveLabel(selectedPeriod)}`;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-slate-50 rounded-2xl w-full max-w-5xl relative max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">

        {/* ── Header ── */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-56 h-56 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${getCompanyBg(director.company)}`}>
                <span className="text-sm font-bold text-white">{getInitials(director.name)}</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white tracking-tight">{director.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${getCompanyBadgeClass(director.company)}`}>
                    {director.company}
                  </span>
                  <span className="text-sm text-slate-400">{getPeriodLabel()}</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center mb-3">
                <Target size={15} className="text-indigo-600" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">{objectiveLabel}</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(objective)}</p>
              <div className="mt-2 h-1 bg-indigo-100 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-400 rounded-full w-full" />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center mb-3">
                <Euro size={15} className="text-emerald-600" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Hono Réalisé</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(director.total_sales)}</p>
              <div className="mt-2 h-1 bg-emerald-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 rounded-full w-full" />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mb-3">
                <TrendingUp size={15} className="text-amber-600" />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">Hono Potentiel</p>
              <p className="text-xl font-bold text-slate-800">{formatCurrency(director.potential_sales)}</p>
              <div className="mt-2 h-1 bg-amber-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full w-full" />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${
                completionRate >= 100 ? 'bg-emerald-50' : completionRate >= 75 ? 'bg-blue-50' :
                completionRate >= 50  ? 'bg-amber-50' : 'bg-red-50'
              }`}>
                <BarChart3 size={15} className={
                  completionRate >= 100 ? 'text-emerald-600' : completionRate >= 75 ? 'text-blue-600' :
                  completionRate >= 50  ? 'text-amber-600' : 'text-red-500'
                } />
              </div>
              <p className="text-xs font-medium text-slate-500 mb-1">% Réalisation</p>
              <p className={`text-xl font-bold ${
                completionRate >= 100 ? 'text-emerald-600' : completionRate >= 75 ? 'text-blue-600' :
                completionRate >= 50  ? 'text-amber-600' : 'text-red-500'
              }`}>{completionRate.toFixed(1)}%</p>
              <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    completionRate >= 100 ? 'bg-emerald-400' : completionRate >= 75 ? 'bg-blue-400' :
                    completionRate >= 50  ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ width: `${Math.min(completionRate, 100)}%` }}
                />
              </div>
            </div>
          </div>

          {/* ── Agences ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
              <Building2 size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Détail par agence</span>
              <span className="ml-auto text-xs text-slate-400">{director.agencies.length} agence{director.agencies.length > 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Agence', objectiveLabel, 'Hono Potentiel', 'Hono Réalisé', 'Reste', '% Réalisation', 'Trans.'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {director.agencies.map(agency => {
                    const agcObj = calculateObjective(agency.annual_objective);
                    const agcRate = agcObj ? (agency.total_sales / agcObj) * 100 : 0;
                    const agcRemaining = agcObj - agency.total_sales;
                    return (
                      <tr key={agency.name} className="border-b border-slate-50 last:border-0 hover:bg-indigo-50/20 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${getCompanyBg(director.company)}`}>
                              {getInitials(agency.name)}
                            </div>
                            <span className="text-sm font-semibold text-slate-800">{agency.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{moneyPill(agcObj, 'border-slate-200 text-slate-600 bg-slate-50')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{moneyPill(agency.potential_sales, 'border-amber-200 text-amber-700 bg-amber-50')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{moneyPill(agency.total_sales, 'border-emerald-200 text-emerald-700 bg-emerald-50')}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {agcObj ? (
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                              agcRemaining > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            }`}>
                              {agcRemaining > 0 ? '↘' : '↗'} {formatCurrency(Math.abs(agcRemaining))}
                            </span>
                          ) : <span className="text-slate-300 text-sm">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {agcObj ? completionPill(agcRate) : <span className="text-slate-300 text-sm">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-xs font-bold text-slate-600 border border-slate-200">
                            {agency.sales_count}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Négociateurs ── */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-3.5 bg-slate-50 border-b border-slate-100">
              <Users size={15} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Détail par négociateur</span>
              <span className="ml-auto text-xs text-slate-400">
                {director.agencies.reduce((acc, a) => acc + a.negotiators.length, 0)} négociateur{director.agencies.reduce((acc, a) => acc + a.negotiators.length, 0) > 1 ? 's' : ''}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    {['Négociateur', 'Agence', objectiveLabel, 'Hono Réalisé', 'Reste', '% Réalisation', 'Trans.'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {director.agencies.flatMap(agency =>
                    agency.negotiators.map(negotiator => {
                      const negObj = calculateObjective(negotiator.annual_objective);
                      const negRate = negObj ? (negotiator.total_sales / negObj) * 100 : 0;
                      const negRemaining = negObj - negotiator.total_sales;
                      return (
                        <tr key={`${agency.name}-${negotiator.id}`} className="border-b border-slate-50 last:border-0 hover:bg-indigo-50/20 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-slate-400 flex items-center justify-center text-[10px] font-bold text-white shrink-0">
                                {getInitials(negotiator.name)}
                              </div>
                              <span className="text-sm font-semibold text-slate-800">{negotiator.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${getCompanyBadgeClass(director.company)}`}>
                              {agency.name}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{moneyPill(negObj, 'border-slate-200 text-slate-600 bg-slate-50')}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{moneyPill(negotiator.total_sales, 'border-emerald-200 text-emerald-700 bg-emerald-50')}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {negObj ? (
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                negRemaining > 0 ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}>
                                {negRemaining > 0 ? '↘' : '↗'} {formatCurrency(Math.abs(negRemaining))}
                              </span>
                            ) : <span className="text-slate-300 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {negObj ? completionPill(negRate) : <span className="text-slate-300 text-sm">—</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-xs font-bold text-slate-600 border border-slate-200">
                              {negotiator.sales_count}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
